import { ReportSdk } from 'lib-klab-sdks';

type DateRange = { startDate: string; endDate?: string };

export const timeSheetReportsSdkMock = {
    getTimeSheet: (
        employeeId = 123,
        dateRange: DateRange = buildDefaultLast30DaysRange(),
        page = 0,
        size = 20,
    ): ReportSdk.TimeSheetSummaryResponse => {
        const normalizedRange = normalizeRange(dateRange);

        const all = generateTimesheetItemsForRange(normalizedRange);

        const totalElements = all.length;
        const safePage = Math.max(0, Number(page) || 0);
        const safeSize = Math.max(1, Number(size) || 20);
        const totalPages = Math.max(1, Math.ceil(totalElements / safeSize));

        const start = safePage * safeSize;
        const end = start + safeSize;

        return {
            metadata: {
                employeeId,
                dateRange: normalizedRange,
            },
            content: all.slice(start, end),
            page: {
                page: safePage,
                size: safeSize,
                totalElements,
                totalPages,
            },
        } as ReportSdk.TimeSheetSummaryResponse;
    },
};

function generateTimesheetItemsForRange(
    dateRange: Required<DateRange>,
): ReportSdk.TimeSheetSummaryItem[] {
    const start = parseYmd(dateRange.startDate);
    const end = parseYmd(dateRange.endDate);

    const days = enumerateDaysInclusive(start, end);

    const result: ReportSdk.TimeSheetSummaryItem[] = [];

    for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const dateISO = toYmd(day);

        const totalSeconds = 8 * 3600;

        const officeSeconds = clamp(
            totalSeconds - jitterSeconds(0, i, 0, 20 * 60),
            6 * 3600,
            totalSeconds,
        );

        const idleSeconds = clamp(
            jitterSeconds(35 * 60, i, 1, 35 * 60),
            15 * 60,
            2 * 3600,
        );

        const offlineSeconds = clamp(
            jitterSeconds(25 * 60, i, 2, 45 * 60),
            10 * 60,
            2 * 3600,
        );

        const activeSeconds = clamp(officeSeconds - idleSeconds, 3 * 3600, officeSeconds);

        let productiveSeconds = clamp(
            activeSeconds - jitterSeconds(50 * 60, i, 3, 60 * 60),
            2 * 3600,
            activeSeconds,
        );

        let unproductiveSeconds = clamp(
            jitterSeconds(10 * 60, i, 4, 25 * 60),
            0,
            activeSeconds,
        );

        let neutralSeconds = clamp(
            jitterSeconds(15 * 60, i, 5, 25 * 60),
            0,
            activeSeconds,
        );

        const sumA = productiveSeconds + unproductiveSeconds + neutralSeconds;
        if (sumA > activeSeconds) {
            const overflow = sumA - activeSeconds;
            const cutNeutral = Math.min(overflow, neutralSeconds);
            neutralSeconds -= cutNeutral;

            const rem1 = overflow - cutNeutral;
            const cutUnprod = Math.min(rem1, unproductiveSeconds);
            unproductiveSeconds -= cutUnprod;

            const rem2 = rem1 - cutUnprod;
            productiveSeconds = Math.max(0, productiveSeconds - rem2);
        }

        const startHour = 8 + ((i * 3) % 3);
        const startMin = 30 + ((i * 11) % 40);
        const startAt = withTime(day, startHour, startMin % 60, 0);

        const endedAt = addSeconds(startAt, totalSeconds);

        const productivityPercentage =
            officeSeconds > 0
                ? Number(((productiveSeconds / officeSeconds) * 100).toFixed(2))
                : 0;

        result.push({
            date: dateISO,
            startAt: toIsoZ(startAt),
            endedAt: toIsoZ(endedAt),
            totalSeconds,
            officeSeconds,
            activeSeconds,
            productiveSeconds,
            unproductiveSeconds,
            neutralSeconds,
            idleSeconds,
            offlineSeconds,
            productivityPercentage,
            holiday: false,
        } as any);
    }

    result.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    return result;
}

function normalizeRange(range: DateRange): Required<DateRange> {
    const start = parseYmd(range.startDate);
    const end = parseYmd(range.endDate ?? range.startDate);

    const s = start.getTime();
    const e = end.getTime();

    if (s <= e) return { startDate: toYmd(start), endDate: toYmd(end) };
    return { startDate: toYmd(end), endDate: toYmd(start) };
}

function buildDefaultLast30DaysRange(): Required<DateRange> {
    const end = startOfToday();
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    return { startDate: toYmd(start), endDate: toYmd(end) };
}

function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseYmd(ymd: string): Date {
    const [y, m, d] = String(ymd).split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function enumerateDaysInclusive(start: Date, end: Date): Date[] {
    const res: Date[] = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);

    const last = new Date(end);
    last.setHours(0, 0, 0, 0);

    while (cur.getTime() <= last.getTime()) {
        res.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }

    return res;
}

function withTime(day: Date, hh: number, mm: number, ss: number): Date {
    const d = new Date(day);
    d.setHours(hh, mm, ss, 0);
    return d;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function toIsoZ(d: Date): string {
    return new Date(d.getTime()).toISOString();
}

function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function jitterSeconds(base: number, a: number, b: number, spread: number): number {
    const hash = (a * 17 + b * 31 + 97) % 997;
    const delta = (hash % Math.max(1, spread));
    return Math.max(0, Math.floor(base + delta));
}
